import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useImmer } from 'use-immer';

import { addWaiting, createTask, showOrders } from '@webapp/account/src/resources/tasks/async';
import { selectTasksByUuids, selectWaiting } from '@webapp/account/src/resources/tasks/selectors';
import type { SubtaskStatus, Task, TaskCreateProps } from '@webapp/account/src/resources/tasks/task';
import { TaskLocalStatus } from '@webapp/account/src/resources/tasks/task';

import { Alert } from '@webapp/common/components/message';
import { useActions } from '@webapp/common/hooks/use-actions';
import { useDeepSelector } from '@webapp/common/hooks/use-deep-selector';
import { KEEP_DOWNLOAD_LINK_DURATION } from '@webapp/common/lib/const';
import type { LocalKey } from '@webapp/common/lib/storage';
import { LocalStorage } from '@webapp/common/lib/storage';

interface SavedTask {
    taskUUID: string;
    taskId: number;
    link: string;
    size: number;
    task: Task;
    params?: AnyObject;
}

export type CreateTaskParams = Omit<TaskCreateProps, 'uuid'>;

// TODO implement auto-download option

let timer;

export const useSavedTask = (
    filtersHash: number,
    key: string,
): {
    createTask: (taskProps: CreateTaskParams) => string;
    task: Task | null;
    complete: boolean;
    link: string;
    size: number;
    status: TaskLocalStatus;
    taskStatus: SubtaskStatus;
    progress: number;
    forget: () => void;
} => {
    const linkId = useMemo(
        () =>
            typeof window !== 'undefined'
                ? new URLSearchParams(window.location.search).get('linkId') || undefined
                : undefined,
        [],
    );
    const [taskUUID, setTaskUUID] = useState<string>(null);
    const [localUuids, setLocalUuids] = useImmer<Array<string>>([]);
    const tasks = useDeepSelector(selectTasksByUuids(localUuids));
    const waiting = useSelector(selectWaiting);
    const activeTask = localUuids.length > 0 && taskUUID ? tasks[taskUUID] : null;
    const [savedState, setSavedState] = useState<SavedTask>(null);
    const [task, setTask] = useState(activeTask);
    const status = task?.status;
    const complete = Boolean(savedState?.link || status === TaskLocalStatus.COMPLETED);
    const link = (task?.results?.link || savedState?.link) as string;
    const size = (task?.results?.size || savedState?.size) as number;
    const taskStatus = task?.results?.status as SubtaskStatus;
    const taskId = task?.id;
    let progress = Math.floor(100 * (task?.position / task?.total));
    progress = isFinite(progress) ? progress : 0;
    const taskKey: LocalKey = `task_${key}_${filtersHash}`;
    const prevTaskRef = useRef(taskKey);

    const createTaskRequest = useActions(createTask);
    const addWaitingRequest = useActions(addWaiting);
    const showOrdersRequest = useActions(showOrders);

    const loadTask = useCallback(
        (taskKey) => {
            const saved = LocalStorage.get<SavedTask>(taskKey);

            if (saved) {
                setSavedState(saved);
                setTask(saved.task);

                const complete = Boolean(saved.link);

                if (!complete) {
                    // resume progress for unfinished task
                    setTaskUUID(saved.taskUUID);
                    addWaitingRequest(saved.taskUUID, saved.task);
                }
            }
        },
        [addWaitingRequest],
    );

    const requestWithTimer = useCallback(() => {
        const id = Object.values(waiting);

        if (id.length > 0) {
            timer = setTimeout(() => {
                showOrdersRequest({
                    limit: 100,
                    id,
                }).then(() => {
                    requestWithTimer();
                });
            }, 3000);
        }
    }, [waiting, showOrdersRequest]);

    const handleCreateTask = useCallback(
        (taskProps: CreateTaskParams) => {
            const uuid = nanoid();
            setLocalUuids((draft) => {
                draft.push(uuid);
            });

            createTaskRequest({ ...taskProps, uuid, linkId });
            return uuid;
        },
        [createTaskRequest, setLocalUuids, linkId],
    );

    const saveTask = useCallback(
        (taskUUID) => {
            const saved = {
                taskUUID,
                taskId,
                link,
                size,
                task,
            };
            if (taskId) {
                LocalStorage.set(taskKey, saved, KEEP_DOWNLOAD_LINK_DURATION * 60);
            }
            setSavedState(saved);
        },
        [taskId, link, size, task, taskKey],
    );

    const createSavedTask = useCallback(
        (data: TaskCreateProps): string => {
            const uuid = handleCreateTask(data);

            saveTask(uuid);
            setTaskUUID(uuid);

            return uuid;
        },
        [handleCreateTask, saveTask],
    );

    const forget = useCallback(
        (erase = true) => {
            erase && LocalStorage.remove(taskKey);
            setTaskUUID(null);
            setTask(null);
            setSavedState(null);
        },
        [taskKey],
    );

    useEffect(() => {
        if (/*activeTask?.status !== TaskLocalStatus.ERROR && */ activeTask) {
            setTask(activeTask);
        }
    }, [activeTask, forget]);

    useEffect(() => {
        // update task details
        if (task && taskUUID && taskId && prevTaskRef.current === taskKey) {
            saveTask(taskUUID);
        }
    }, [saveTask, task, taskId, taskKey, taskUUID]);

    useEffect(() => {
        if (prevTaskRef.current !== taskKey) {
            forget(false);
            prevTaskRef.current = taskKey;
            loadTask(taskKey);
        }
    }, [forget, loadTask, taskKey]);

    useEffect(() => {
        switch (status) {
            case TaskLocalStatus.ERROR:
                Alert.error('Ошибка скачивания');
                forget();
                break;
        }
    }, [forget, status]);

    useEffect(() => {
        clearTimeout(timer);
        requestWithTimer();

        return () => {
            clearTimeout(timer);
        };
    }, [requestWithTimer]);

    return useMemo(
        () => ({
            createTask: createSavedTask,
            complete,
            link,
            size,
            task,
            progress,
            status,
            taskStatus,
            forget,
        }),
        [complete, createSavedTask, forget, link, progress, size, status, task, taskStatus],
    );
};
